<template>
  <div class="census-boundary-picker">
    <button type="button" class="census-boundary-picker__toggle" :aria-expanded="isExpanded" aria-controls="census-boundary-content" @click="isExpanded = !isExpanded">
      <span>Use a city, town, or county boundary</span>
      <small v-if="wasApplied && preview">{{ preview.boundary.label }} · {{ preview.diagnostics.outputVertexCount }} points applied</small>
      <small v-else-if="manualSelected">Manual drawing</small>
      <span class="census-boundary-picker__toggle-action"
        >{{ isExpanded ? "Collapse" : "Expand" }} <span aria-hidden="true">{{ isExpanded ? "▴" : "▾" }}</span></span
      >
    </button>
    <div id="census-boundary-content" v-show="isExpanded" class="census-boundary-picker__content">
      <form class="census-boundary-picker__search" @submit.prevent="search(false)">
        <input v-model="searchState" class="census-boundary-picker__state" maxlength="2" placeholder="State" aria-label="Census state" />
        <input v-model="query" class="census-boundary-picker__name" placeholder="City, town, or county" aria-label="Census name" />
        <select v-model="kind" class="census-boundary-picker__type" aria-label="Census boundary type">
          <option value="">All types</option>
          <option value="place">City / place</option>
          <option value="cousub">Town / township</option>
          <option value="county">County</option>
        </select>
        <button type="submit" class="ui small button" :disabled="isSearching">{{ isSearching ? "Searching…" : "Search" }}</button>
      </form>
      <p v-if="message" class="census-boundary-picker__message" role="status">{{ message }}</p>
      <div class="census-boundary-picker__results">
        <button
          v-for="match in matches"
          :key="`${match.boundary.kind}-${match.boundary.geoid}`"
          type="button"
          :class="{ 'census-boundary-picker__result--selected': selected?.geoid === match.boundary.geoid && selected?.kind === match.boundary.kind }"
          @click="select(match.boundary)"
        >
          {{ match.boundary.label }}, {{ match.boundary.state }}
          <small>{{ typeLabel(match.boundary.kind) }}{{ match.boundary.countyName && match.boundary.kind !== "county" ? ` · ${match.boundary.countyName}` : "" }}</small>
        </button>
        <button type="button" class="census-boundary-picker__manual" :class="{ 'census-boundary-picker__result--selected': manualSelected }" @click="drawManually">Manually draw boundary</button>
      </div>
    </div>
    <div v-if="selected" class="census-boundary-picker__applied">
      <div class="census-boundary-picker__points">
        <div class="census-boundary-picker__presets">
          <button v-for="limit in [30, 10, 4]" :key="limit" type="button" class="ui mini button" :class="{ 'census-boundary-picker__preset--active': maxVertices === limit }" @click="setLimit(limit)">
            {{ limit }} points
          </button>
        </div>
        <label>Max points <input v-model.number="maxVertices" type="number" min="4" max="50" step="1" @change="loadPreview" /></label>
      </div>
      <p v-if="isLoading" role="status">Preparing the boundary preview…</p>
      <p v-if="message && !isExpanded" class="census-boundary-picker__message" role="status">{{ message }}</p>
      <template v-if="preview && !isLoading && isExpanded">
        <svg v-if="outlines" class="census-boundary-picker__preview" viewBox="0 0 800 320" role="img" :aria-label="`Census and trimmed boundary comparison for ${preview.boundary.label}`">
          <path :d="outlines.trimmed" class="census-boundary-picker__trimmed" />
          <path :d="outlines.original" class="census-boundary-picker__original" />
        </svg>
        <div class="census-boundary-picker__legend">
          <span><i class="census-boundary-picker__original-key" aria-hidden="true"></i> Census boundary</span>
          <span><i class="census-boundary-picker__trimmed-key" aria-hidden="true"></i> Trimmed boundary</span>
        </div>
        <p>
          {{ preview.diagnostics.originalVertexCount }} → {{ preview.diagnostics.outputVertexCount }} points · {{ preview.diagnostics.extraAreaPercent.toFixed(1) }}% extra area ·
          {{ preview.diagnostics.excludedAreaPercent }}% excluded
        </p>
        <p v-if="preview.diagnostics.fallbackReason" class="census-boundary-picker__warning">{{ preview.diagnostics.fallbackReason }}</p>
        <button type="button" class="ui small primary button" @click="apply">Apply boundary</button>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from "vue";
import axios from "axios";

interface BoundaryEntry {
  kind: string;
  geoid: string;
  name: string;
  label: string;
  state: string;
  countyName: string | null;
}
interface BoundaryPreview {
  boundary: BoundaryEntry;
  originalGeoJson: string;
  boundaryGeoJson: string;
  diagnostics: {
    originalVertexCount: number;
    outputVertexCount: number;
    outputAreaSquareMiles: number;
    excludedAreaPercent: number;
    extraAreaPercent: number;
    method: string;
    fallbackReason: string | null;
  };
}

const props = defineProps<{ state: string | null; latitude: number | null; longitude: number | null; suggestionNames: string[]; countyName: string | null }>();
const emit = defineEmits<{
  (e: "applyBoundary", geoJson: string, areaSquareMiles: number): void;
  (e: "manualBoundary"): void;
}>();
const searchState = ref("");
const query = ref("");
const kind = ref("");
const maxVertices = ref(30);
const matches = ref<{ boundary: BoundaryEntry; rank: number }[]>([]);
const selected = ref<BoundaryEntry | null>(null);
const preview = ref<BoundaryPreview | null>(null);
const message = ref("");
const isSearching = ref(false);
const isLoading = ref(false);
const wasApplied = ref(false);
const isExpanded = ref(true);
const manualSelected = ref(false);
let searchRequest = 0;
let previewRequest = 0;

// Both outlines share one display transform; rounding here affects only the SVG, never the saved boundary.
const outlines = computed(() => {
  if (!preview.value) return null;
  const rings = (json: string): number[][][] => {
    const geometryRings = (geometry): number[][][] => (geometry.type === "Polygon" ? geometry.coordinates : geometry.coordinates.flat());
    const value = JSON.parse(json);
    return value.type === "FeatureCollection" ? value.features.flatMap(feature => geometryRings(feature.geometry)) : geometryRings(value.type === "Feature" ? value.geometry : value);
  };
  const original = rings(preview.value.originalGeoJson);
  const trimmed = rings(preview.value.boundaryGeoJson);
  let west = Infinity,
    east = -Infinity,
    south = Infinity,
    north = -Infinity;
  for (const ring of [...original, ...trimmed]) {
    for (const [longitude, latitude] of ring) {
      west = Math.min(west, longitude);
      east = Math.max(east, longitude);
      south = Math.min(south, latitude);
      north = Math.max(north, latitude);
    }
  }
  const longitudeScale = Math.cos((((south + north) / 2) * Math.PI) / 180);
  const width = (east - west) * longitudeScale;
  const height = north - south;
  const scale = Math.min(760 / Math.max(width, 1e-12), 280 / Math.max(height, 1e-12));
  const path = (boundaryRings: number[][][]) =>
    boundaryRings
      .map(
        ring =>
          ring
            .map(
              ([longitude, latitude], index) =>
                `${index ? "L" : "M"}${(400 + ((longitude - west) * longitudeScale - width / 2) * scale).toFixed(2)},${(160 - (latitude - south - height / 2) * scale).toFixed(2)}`
            )
            .join(" ") + " Z"
      )
      .join(" ");
  return { original: path(original), trimmed: path(trimmed) };
});

function invalidatePreview() {
  previewRequest++;
  preview.value = null;
  selected.value = null;
  isLoading.value = false;
  wasApplied.value = false;
}

watch(
  () => [props.state, props.latitude, props.longitude, props.suggestionNames],
  () => {
    isExpanded.value = true;
    manualSelected.value = false;
    searchState.value = props.state ?? "";
    query.value = props.suggestionNames.find(n => n !== props.countyName) ?? "";
    kind.value = "";
    invalidatePreview();
    matches.value = [];
    message.value = "";
    if (searchState.value && props.latitude != null) search(true);
  },
  { immediate: true }
);

// Editing the search invalidates any pending response before the next explicit search.
watch(
  [searchState, query, kind],
  () => {
    searchRequest++;
    isSearching.value = false;
    matches.value = [];
    invalidatePreview();
    message.value = "";
  },
  { flush: "sync" }
);

onBeforeUnmount(() => {
  searchRequest++;
  previewRequest++;
});

async function search(useSuggestions: boolean) {
  const request = ++searchRequest;
  invalidatePreview();
  matches.value = [];
  message.value = "";
  if (!/^[a-z]{2}$/i.test(searchState.value.trim())) {
    message.value = "Enter a two-letter US state or territory.";
    return;
  }
  const params = new URLSearchParams({ state: searchState.value.trim().toUpperCase(), kind: kind.value });
  for (const name of useSuggestions ? props.suggestionNames : [query.value]) if (name.trim()) params.append("names", name);
  if (useSuggestions && props.countyName) params.set("countyName", props.countyName);
  if (props.latitude != null && props.longitude != null) {
    params.set("latitude", String(props.latitude));
    params.set("longitude", String(props.longitude));
  }
  isSearching.value = true;
  try {
    const { data } = await axios.get(`${import.meta.env.VITE_API_URL}/customer-boundaries/search?${params}`);
    if (request !== searchRequest) return;
    matches.value = data.matches;
    maxVertices.value = data.defaultMaxVertices;
    if (!matches.value.length) message.value = "No matching Census boundaries. Try another name or draw the boundary below.";
  } catch (error) {
    if (request === searchRequest) message.value = error.response?.data?.message ?? "Census boundaries are unavailable. You can still draw the boundary below.";
  } finally {
    if (request === searchRequest) isSearching.value = false;
  }
}

function select(entry: BoundaryEntry) {
  manualSelected.value = false;
  selected.value = entry;
  loadPreview();
}

function drawManually() {
  invalidatePreview();
  manualSelected.value = true;
  message.value = "";
  isExpanded.value = false;
  emit("manualBoundary");
}

function setLimit(limit: number) {
  maxVertices.value = limit;
  loadPreview();
}

async function loadPreview() {
  const request = ++previewRequest;
  preview.value = null;
  wasApplied.value = false;
  isLoading.value = false;
  if (!selected.value) return;
  isExpanded.value = true;
  if (!Number.isInteger(maxVertices.value) || maxVertices.value < 4 || maxVertices.value > 50) {
    message.value = "Choose a whole-number point limit from 4 to 50.";
    return;
  }
  message.value = "";
  isLoading.value = true;
  try {
    const { data } = await axios.get(`${import.meta.env.VITE_API_URL}/customer-boundaries/${selected.value.kind}/${selected.value.geoid}`, {
      params: { state: selected.value.state, maxVertices: maxVertices.value },
    });
    if (request !== previewRequest) return;
    preview.value = data;
  } catch (error) {
    if (request === previewRequest) message.value = error.response?.data?.message ?? "This boundary could not be prepared. Try another result or draw it below.";
  } finally {
    if (request === previewRequest) isLoading.value = false;
  }
}

function apply() {
  if (!preview.value || isLoading.value) return;
  emit("applyBoundary", preview.value.boundaryGeoJson, preview.value.diagnostics.outputAreaSquareMiles);
  wasApplied.value = true;
  isExpanded.value = false;
}

function typeLabel(value: string): string {
  return value === "county" ? "County" : value === "cousub" ? "County subdivision" : "City / place";
}
</script>

<style scoped lang="scss">
.census-boundary-picker {
  margin: 12px 0 16px;
  padding: 0;
  border: 1px solid #d6dce0;
  border-radius: 4px;
  background: #fafbfc;

  &__toggle {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px 12px;
    width: 100%;
    margin: 0;
    padding: 12px;
    border: 0;
    border-radius: 4px;
    background: transparent;
    color: #35434d;
    font: inherit;
    font-size: 13px;
    font-weight: 600;
    text-align: left;
    cursor: pointer;
  }
  &__toggle:hover {
    background: #f0f4f6;
  }
  &__toggle > span:first-child {
    min-width: 0;
    white-space: normal;
  }
  &__toggle small {
    color: #147d8b;
    font-size: 12px;
    font-weight: 500;
  }
  &__toggle-action {
    margin-left: auto;
    font-size: 12px;
    font-weight: 500;
    color: #526975;
    white-space: nowrap;
  }
  &__content {
    padding: 0 12px 12px;
  }
  &__search {
    display: grid;
    grid-template-columns: 60px minmax(120px, 280px) minmax(140px, 190px) max-content;
    gap: 8px;
    align-items: center;
    margin: 0;
  }
  &__points,
  &__presets {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
  }
  &__points {
    justify-content: space-between;
  }
  &__presets {
    gap: 6px;
  }
  &__search input,
  &__search select,
  &__points input {
    box-sizing: border-box;
    min-width: 0;
    width: 100%;
    min-height: 34px;
    margin: 0;
    padding: 6px 8px;
    border: 1px solid #cbd1d6;
    border-radius: 4px;
    background: white;
    font-size: 13px;
    line-height: 20px;
  }
  &__search .button,
  &__points .button {
    margin: 0;
  }
  &__points label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    width: auto;
    margin: 0 0 0 auto;
    font-size: 12px;
    font-weight: 500;
  }
  &__points input {
    width: 68px;
  }
  &__preset--active {
    box-shadow: inset 0 0 0 1px #147d8b !important;
    color: #147d8b !important;
  }
  &__results {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 10px;
  }
  &__results button {
    background: white;
    border: 1px solid #c8ced3;
    border-radius: 4px;
    padding: 7px 10px;
    text-align: left;
    font-size: 13px;
    cursor: pointer;
  }
  &__results small {
    display: block;
    color: #5a6269;
  }
  &__result--selected {
    outline: 2px solid #147d8b;
  }
  &__manual {
    border-style: dashed !important;
    color: #526975;
  }
  &__applied {
    padding: 12px;
    border-top: 1px solid #e1e5e8;
  }
  &__message,
  &__warning {
    margin-top: 12px;
  }
  &__warning {
    color: #8a4b0a;
  }
  &__applied p {
    margin: 8px 0;
    font-size: 12px;
    line-height: 18px;
  }
  &__preview {
    display: block;
    width: 100%;
    max-height: 320px;
    margin-top: 12px;
    border: 1px solid #e1e5e8;
    border-radius: 4px;
    background: white;
  }
  &__trimmed {
    fill: #e9a96b;
    fill-opacity: 0.22;
    stroke: #bd641e;
    stroke-width: 2;
    fill-rule: evenodd;
  }
  &__original {
    fill: #48a7b2;
    fill-opacity: 0.15;
    stroke: #147d8b;
    stroke-width: 1.5;
    fill-rule: evenodd;
  }
  &__legend {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 16px;
    margin-top: 8px;
    font-size: 12px;
  }
  &__legend span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  &__legend i {
    width: 16px;
    border-top: 3px solid;
  }
  &__original-key {
    color: #147d8b;
  }
  &__trimmed-key {
    color: #bd641e;
  }
  @media (max-width: 650px) {
    &__search {
      grid-template-columns: 50px minmax(0, 1fr) 110px;
    }
    &__search button {
      grid-column: 1 / -1;
      justify-self: start;
    }
    &__points {
      flex-wrap: nowrap;
      gap: 6px;
      align-items: end;
    }
    &__presets {
      flex-wrap: nowrap;
      gap: 4px;
    }
    &__points label {
      flex-direction: column;
      align-items: flex-start;
      gap: 3px;
      width: 60px;
      font-size: 11px;
      line-height: 14px;
    }
    &__points input {
      width: 60px;
    }
    &__points .button {
      padding: 8px 6px;
      font-size: 11px;
      white-space: nowrap;
    }
    &__toggle {
      display: grid;
      grid-template-columns: minmax(0, 1fr) auto;
      gap: 6px 8px;
    }
    &__toggle-action {
      grid-column: 2;
      grid-row: 1;
    }
    &__toggle small {
      grid-column: 1 / -1;
      width: auto;
    }
  }
}
</style>
